import React, { useState } from "react";
import { ref, set, push, get, update, increment } from "firebase/database";
import { auth, database, firestore } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { TRANSACTION_DIRECTION } from "../../../constants/farmerConstants";

const PaymentModal = ({ farmer, companyName, onClose }) => {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("M-Pesa");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ New: Unified function to get user role and permissions
  const getUserRoleAndPermissions = async (uid, companyName) => {
    try {
      // 1️⃣ Check in Firestore "users"
      const userDoc = await getDoc(doc(firestore, "users", uid));
      if (userDoc.exists()) {
        const data = userDoc.data();

        // If user belongs to a company, check if they are an employee
        if (data.companyName) {
          const employeeDoc = await getDoc(
            doc(firestore, "companies", data.companyName, "employees", uid)
          );
          if (employeeDoc.exists()) {
            return employeeDoc.data();
          }
        }

        return data; // fallback to basic user doc
      }

      // 2️⃣ Check Realtime DB (for mobile-registered users)
      const realtimeSnap = await get(ref(database, `Users/${uid}`));
      if (realtimeSnap.exists()) {
        return realtimeSnap.val();
      }

      return null;
    } catch (error) {
      console.error("Error fetching user role:", error);
      return null;
    }
  };

  const handlePayment = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user found");

      // ✅ Fetch role and permissions properly
      const roleData = await getUserRoleAndPermissions(user.uid, companyName);
      const role =
        roleData?.role || roleData?.accountType || "Unknown";
      const canAddPayments = roleData?.permissions?.canAddPayments ?? false;

      // ✅ Restrict payment access
      if (role !== "CompanyAdmin" && !canAddPayments) {
        toast.error("You don’t have permission to record payments");
        setLoading(false);
        return;
      }

      const now = new Date();
      const year = now.getFullYear();
      const month = now.toLocaleString("en-US", { month: "long" });
      const day = now.getDate();
      const dateStr = now.toISOString();

      const paymentId = push(ref(database)).key;
      const paymentData = {
        Id: paymentId,
        OtherName: farmer.otherName || "",
        CloudName: farmer.name || "",
        Status: "Pending", // ✅ default status
        Details: details || "N/A",
        CompanyName: companyName,
        AuthorizedBy: user.email,
        TransactionDate: dateStr,
        Amount: amount.toString(),
        ReceiptNo: `RCPT-${Math.floor(Math.random() * 100000)}`,
        Mode: farmer.mode,
      };

      // Collect all write promises for parallel execution
      const writePromises = [];

      // 1️⃣ Save to Farmer record
      const clientPath = `Companies/${companyName}/FarmerRecords/${farmer.otherName}/Payments/${year}/${month}/${paymentId}`;
      writePromises.push(set(ref(database, clientPath), paymentData));

      // 2️⃣ Save to Company Payments
      const companyPath = `Companies/${companyName}/Payments/${year}/${month}/${day}/${paymentId}`;
      writePromises.push(set(ref(database, companyPath), paymentData));

      if (mode === "M-Pesa") {
        // 3️⃣ Save to NewPaymentRecord (for approval)
        const newPaymentPath = `Companies/${companyName}/NewPaymentRecord/${paymentId}`;
        writePromises.push(set(ref(database, newPaymentPath), paymentData));
      }

      // 4️⃣ Deduct payment from farmer balance
      const farmerBalancePath = `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}`;
      writePromises.push(update(ref(database, farmerBalancePath), {
        Balance: increment(-parseFloat(amount))
      }));

      // 5️⃣ Record transaction in company FarmerPayable account
      const payableTxPath = `Companies/${companyName}/Accounts/farmer_payable/${year}/${month}/${paymentId}`;
      const payableTxData = {
        id: paymentId,
        amount: parseFloat(amount),
        direction: TRANSACTION_DIRECTION.DEBIT, // debit reduces the payable
        farmerName: farmer.name,
        farmerOtherName: farmer.otherName,
        description: `Payment to ${farmer.name}`,
        date: dateStr,
        processedBy: user.email,
      };
      writePromises.push(set(ref(database, payableTxPath), payableTxData));

      // Execute all writes in parallel for better performance
      await Promise.all(writePromises);


      toast.success("Payment recorded successfully!");
      onClose();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error("Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg w-96 p-6">
        <h2 className="text-lg font-semibold mb-4 text-cyan-700">
          Record Payment for {farmer.name}
        </h2>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium">Amount (KES)</label>
            <input
              type="number"
              className="border rounded-md px-3 py-2 w-full"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Payment Mode</label>
            <select
              className="border rounded-md px-3 py-2 w-full"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <option value="M-Pesa">M-Pesa</option>
              <option value="Cash">Cash</option>
              <option value="Bank">Bank</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Details</label>
            <textarea
              className="border rounded-md px-3 py-2 w-full"
              rows="3"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end mt-5 space-x-2">
          <button
            className="px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-md bg-cyan-600 text-white hover:bg-cyan-700"
            onClick={handlePayment}
            disabled={loading}
          >
            {loading ? "Saving..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
